import { Capacitor } from "@capacitor/core";
import { NativePurchases, PURCHASE_TYPE, type Transaction } from "@capgo/native-purchases";
import type { BillingError, BillingErrorCode, BillingPort, PurchaseResult, StoreProductInfo } from "./types";

/**
 * 네이티브 결제(AC0-179): @capgo/native-purchases — Android Play Billing 8, iOS StoreKit 2.
 * - 자동 acknowledge·finish 를 끈다(autoAcknowledgePurchases:false). Android consume 은 서버(지급 커밋 뒤), iOS finish 는 서버 200 뒤 finish().
 *   단, Android 플러그인은 연결 시 미승인 구매를 acknowledge 한다 — consume 이 아니라 3일 환불만 막으므로 서버 consume 과 충돌하지 않는다.
 * - 강제 종료 복구: Android = 아직 consume 안 된 구매(getPurchases), iOS = 최근 RECOVERY_WINDOW 안의 소모품 거래.
 *   iOS 플러그인은 Transaction.updates 로 온 거래를 즉시 finish 하므로 onPurchaseUpdated 로도 받는다.
 *   Info.plist SKIncludeConsumableInAppPurchaseHistory(iOS 18+)가 finish 된 소모품도 getPurchases 에 남겨 이벤트를 놓친 경우를 메운다.
 */

const store: PurchaseResult["store"] = Capacitor.getPlatform() === "ios" ? "apple" : "google";
const RECOVERY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Android Purchase.PurchaseState.PURCHASED */
const ANDROID_PURCHASED = "1";

function toResult(t: Transaction): PurchaseResult | null {
  if (store === "apple") {
    return t.jwsRepresentation ? { store, productId: t.productIdentifier, signedTransaction: t.jwsRepresentation, transactionId: t.transactionId } : null;
  }
  return t.purchaseToken ? { store, productId: t.productIdentifier, purchaseToken: t.purchaseToken, ...(t.orderId ? { orderId: t.orderId } : {}) } : null;
}

/**
 * 플러그인 reject 문구 → 코드. iOS "User cancelled"·"Transaction pending", Android "Purchase is pending"·"Purchase is not purchased".
 * Android 는 사용자 취소와 그 밖의 결제창 실패를 같은 문구로 돌려주므로 취소로 본다(조용히 종료). 이미 소유(미소비) 상품은 복구 경로가 지급한다.
 */
function toBillingError(e: unknown): BillingError {
  const message = e instanceof Error ? e.message : String(e);
  let code: BillingErrorCode = "failed";
  if (/user cancel|not purchased/i.test(message)) code = "cancelled";
  else if (/pending/i.test(message)) code = "pending";
  else if (/product not found|cannot find product|BILLING_SETUP_FAILED|billing.*unavailable/i.test(message)) code = "unavailable"; // iOS 플러그인 문구는 "Cannot find product for id …"
  return { code, cause: e };
}

/** StoreKit 2 Storefront.countryCode 는 alpha-3 — 서버(billing/region.ts)와 같은 후보 표. 모르는 코드는 서버가 지원 목록에서 걸러낸다 */
const ALPHA3_TO_2: Record<string, string> = { KOR: "KR", JPN: "JP", TWN: "TW", USA: "US", HKG: "HK", SGP: "SG", THA: "TH", VNM: "VN", IDN: "ID", PHL: "PH", MYS: "MY", GBR: "GB", DEU: "DE", FRA: "FR", CAN: "CA", AUS: "AU" };
export const toAlpha2 = (raw: string | undefined | null): string | null => {
  const v = (raw ?? "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(v)) return v;
  if (/^[A-Z]{3}$/.test(v)) return ALPHA3_TO_2[v] ?? v;
  return null;
};

export const nativeBilling: BillingPort = {
  // 스토어 국가는 사용자가 바꿀 수 있어 저장하지 않고 매번 읽는다(플러그인 문서). 못 읽으면 null → 서버가 접속 국가·KR 로 폴백
  async getStorefront() {
    try {
      const { countryCode } = await NativePurchases.getStorefront();
      return toAlpha2(countryCode);
    } catch {
      return null;
    }
  },

  async getProducts(productIds): Promise<StoreProductInfo[]> {
    if (productIds.length === 0) return [];
    const { products } = await NativePurchases.getProducts({ productIdentifiers: productIds, productType: PURCHASE_TYPE.INAPP });
    return products.map((p) => ({ productId: p.identifier, priceString: p.priceString, currency: p.currencyCode }));
  },

  async purchase(productId) {
    let t: Transaction;
    try {
      t = await NativePurchases.purchaseProduct({ productIdentifier: productId, productType: PURCHASE_TYPE.INAPP, isConsumable: false, autoAcknowledgePurchases: false });
    } catch (e) {
      throw toBillingError(e);
    }
    const result = toResult(t);
    if (!result) throw { code: "failed", cause: new Error("purchase returned no token") } satisfies BillingError;
    return result;
  },

  async finish(result) {
    if (result.store !== "apple") return;
    // iOS acknowledgePurchase = transaction.finish(). purchaseToken 자리에 transactionId
    await NativePurchases.acknowledgePurchase({ purchaseToken: result.transactionId });
  },

  async getPendingPurchases() {
    const { purchases } = await NativePurchases.getPurchases({ productType: PURCHASE_TYPE.INAPP });
    const since = Date.now() - RECOVERY_WINDOW_MS;
    return purchases
      .filter((t) => (store === "google" ? t.purchaseState === ANDROID_PURCHASED : !t.revocationDate && Date.parse(t.purchaseDate) >= since))
      .map(toResult)
      .filter((r): r is PurchaseResult => r !== null);
  },

  onPurchaseUpdated(cb) {
    if (store !== "apple") return () => undefined;
    const handle = NativePurchases.addListener("transactionUpdated", (t) => {
      const result = toResult(t);
      if (result) cb(result);
    });
    return () => void handle.then((h) => h.remove());
  },
};
